import React, { useEffect, useState } from 'react';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Checkbox, IconButton } from '@mui/material';
import type { IHeaderParams, SelectAllMode } from 'ag-grid-community';
import styles from './SelectionHeader.module.scss';

const collapseText = 'Collapse all rows';
const expandText = 'Expand next row level';

const EXPANSION_STATE = {
  COLLAPSED: 0,
  PARTIALLY_EXPANDED: 1,
  FULLY_EXPANDED: 2,
} as const;

type ExpansionState = typeof EXPANSION_STATE[keyof typeof EXPANSION_STATE];

interface SelectionHeaderProps extends IHeaderParams {
  showTreeToggle?: boolean;
  showSelectAll?: boolean;
  selectAllMode?: SelectAllMode;
}

const SelectionHeader: React.FC<SelectionHeaderProps> = ({ api, showTreeToggle, showSelectAll, selectAllMode }) => {
  const [selectionState, setSelectionState] = useState({ selected: 0, selectable: 0 });
  const [expansionState, setExpansionState] = useState<ExpansionState>(EXPANSION_STATE.COLLAPSED);

  useEffect(() => {
    const updateSelectionState = () => {
      let selectable = 0;
      let selected = 0;
      const iterate = selectAllMode === 'filtered'
        ? api.forEachNodeAfterFilter.bind(api)
        : api.forEachNode.bind(api);

      iterate((node) => {
        if (node.selectable) {
          selectable += 1;

          if (node.isSelected()) selected += 1;
        }
      });

      setSelectionState({ selected, selectable });
    };

    updateSelectionState();
    api.addEventListener('selectionChanged', updateSelectionState);
    api.addEventListener('filterChanged', updateSelectionState);

    return () => {
      api.removeEventListener('selectionChanged', updateSelectionState);
      api.removeEventListener('filterChanged', updateSelectionState);
    };
  }, [api, selectAllMode]);

  const allSelected = selectionState.selectable > 0 && selectionState.selected === selectionState.selectable;

  return (
    <div className={styles.SelectionHeader}>
      {showSelectAll && (
        <Checkbox
          size="medium"
          checked={allSelected}
          indeterminate={selectionState.selected > 0 && !allSelected}
          onChange={(event) => {
            event.stopPropagation();

            if (selectionState.selected > 0) api.deselectAll(selectAllMode);
            else api.selectAll(selectAllMode);
          }}
        />
      )}
      {showTreeToggle && (
        <IconButton
          size="small"
          aria-label={expansionState === EXPANSION_STATE.FULLY_EXPANDED ? collapseText : expandText}
          onClick={(event) => {
            event.stopPropagation();
            const nextState = expansionState === EXPANSION_STATE.FULLY_EXPANDED
              ? EXPANSION_STATE.COLLAPSED
              : ((expansionState + 1) as ExpansionState);

            api.forEachNode((node) => {
              const row = node.data as Record<string, unknown> | undefined;
              if (!row?.childRows) return;
              const isNestedRow = row.isSubCRChildren === true;
              if (nextState === EXPANSION_STATE.PARTIALLY_EXPANDED) node.setExpanded(!isNestedRow);
              else if (nextState === EXPANSION_STATE.FULLY_EXPANDED) node.setExpanded(true);
              else node.setExpanded(false);
            });
            setExpansionState(nextState);
          }}
        >
          {expansionState === EXPANSION_STATE.FULLY_EXPANDED
            ? <UnfoldLessIcon fontSize="small" />
            : <UnfoldMoreIcon fontSize="small" />}
        </IconButton>
      )}
    </div>
  );
};

export default SelectionHeader;
